import { FC, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@mui/material';
import _ from 'lodash';
import { parseISO } from 'date-fns';
import Highcharts, { Options, SeriesBarOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DeviceDetailedHealth, TelemetryHistoryEntry } from 'types';
import styles from './DeviceHealthTelemetryCard.module.scss';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';

interface DeviceHealthTelemetryCardProps {
    health: DeviceDetailedHealth | null;
}

const FW_ERROR_CODES = [
    'Empty error',
    'DNS error',
    'Modem restart fail',
    'OTA download failure',
    'Modem start failure',
    'Modem tower failure',
    'Fast fail',
    'Bad HTTP response',
    'Failed HTTP POST',
    'Telemetry read failure',
    'Threshold send failure',
    'Low battery',
    'Modem shutdown failure',
    'Failed to write OTA flash',
    'Unusual magnet press',
    'LED driver failure',
    'Bad temperature reading',
    'Bad sensor reading',
    'Inspection mode',
    'Assembly mode',
    'RF test mode ',
    'Terminal mode',
    'Modem configuration failure',
    'Network "fix problems" failure',
    'Network get UTC failure',
    'Telemetry check failure',
    'I2C timeout on read start',
    'I2C timeout on read address',
    'I2C timeout on read byte',
    'I2C timeout on write start',
    'I2C timeout on write address',
    'I2C timeout on write byte',
    '"Serial killer" bug',
    'Repeat wakeup',
    'Unexpected wakeup',
];

const getTelemetryLine = (telemetry?: TelemetryHistoryEntry[]) => {
    if (!telemetry) return [];
    const output: SeriesBarOptions[] = [];

    const allCodes = new Set<number>();
    const codeData: { [key: string]: { time: number; err: number }[] } = {};
    for (const entry of telemetry) {
        if (entry.errors) {
            for (const error of entry.errors) {
                allCodes.add(error);
            }
        }
    }
    for (const code of Array.from<number>(allCodes)) {
        for (const entry of telemetry) {
            const newEntry = { time: Number(parseISO(entry.time)), err: 0 };
            if (entry.errors) {
                const errorCount = _.countBy(entry.errors);
                newEntry.err = errorCount[code];
            }
            if (!codeData[code.toString()]) codeData[code.toString()] = [];
            codeData[code.toString()].push(newEntry);
        }
    }
    for (const code in codeData) {
        const subOutput: SeriesBarOptions = {
            type: 'bar',
            name: FW_ERROR_CODES[parseInt(code)],
            data: [],
        };
        for (const entry of codeData[code]) {
            if (!subOutput.data) subOutput.data = [];
            subOutput.data.push({ x: entry.time, y: entry.err });
        }
        output.push(subOutput);
    }
    return output;
};

const DeviceHealthTelemetryCard: FC<DeviceHealthTelemetryCardProps> = ({
    health,
}) => {
    Highcharts.defaultOptions.accessibility = {
        enabled: false,
    };

    const [options, setOptions] = useState<Options>({});
    const { ui } = useUi();

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    useEffect(() => {
        if (!health) return;
        const tempSeries = getTelemetryLine(health.telemetry_errors);
        setOptions({
            chart: {
                type: 'column',
            },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                title: {
                    text: '',
                },
            },
            plotOptions: {
                series: {
                    stacking: 'normal',
                },
            },
            title: { text: '' },
            credits: { enabled: false },
            series: tempSeries,
            tooltip: {
                valueDecimals: 1,
                xDateFormat: '%b %e %l:%M %p',
            },
        });
    }, [health]);

    return (
        <div
            className={styles.DeviceHealthTelemetryCard}
            data-testid="DeviceHealthTelemetryCard"
        >
            <Card>
                <CardHeader title="Telemetry Error" />
                <CardContent>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                    />
                </CardContent>
            </Card>
        </div>
    );
};
export default DeviceHealthTelemetryCard;
